/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { t } from "i18next";
import React from "react";

interface PowderLotSelectionProps {
  batchId: string;
  incubationData: any;
  setIncubationData: React.Dispatch<React.SetStateAction<any>>;
  powderData: any;
  setPowderData: React.Dispatch<React.SetStateAction<any>>;
  availableLots: any[];
  selectedLotInfo: any;
  batchDetails: any;
  errors: any;
  setErrors: React.Dispatch<React.SetStateAction<any>>;
}

const PowderLotSelection: React.FC<PowderLotSelectionProps> = ({
  batchId,
  incubationData,
  setIncubationData,
  powderData,
  setPowderData,
  availableLots,
  selectedLotInfo,
  batchDetails,
  errors,
  setErrors
}) => {
    return (
        <>
        {/* Section 0: Powder Lot Selection */}
          <section className="bg-white rounded-[1rem] border border-slate-200 shadow-sm overflow-hidden animate-in fade-in duration-500">
            <div className="px-6 py-1.5 border-b border-slate-100 bg-slate-50/30 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 rounded-xl bg-[#2D3E50] text-white flex items-center justify-center text-sm font-black shadow-sm">01</div>
                <h3 className="text-xs font-black text-[#2D3E50] uppercase tracking-[0.2em]">{t("gptTest.gptTestDetails.powderLotDetails")}</h3>
              </div>
            </div>
            <div className="p-6 pt-2 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                {/* Read-Only Info */}
                <div className="grid grid-cols-2 gap-8 bg-slate-50/50 p-8 rounded-3xl border border-slate-100 shadow-inner">
                  <div className="space-y-1">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t("gptTest.gptTestDetails.media_name")}</p>
                    <p className="text-sm text-slate-800">{batchDetails?.name || '--'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t("gptTest.gptTestDetails.batch_no")}</p>
                    <p className="text-sm text-slate-800">{batchDetails?.batchCode || '--'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t("gptTest.gptTestDetails.manufacturer")}</p>
                    <p className="text-sm text-slate-800">{batchDetails?.manufacturer || '--'}</p>
                  </div>
                  {/* <div className="space-y-1">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t("gptTest.gptTestDetails.inventoryRef")}</p>
                    <p className="text-sm font-black text-slate-800">{batchDetails?.inventoryRef || '--'}</p>
                  </div> */}
                </div>

                {/* Lot Selection & Qnty */}
                <div className="space-y-3">
                  <div className="space-y-3">
                    <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">{t("gptTest.gptTestDetails.gptTestDate")} <span className="text-red-500">*</span></label>
                    <input 
                      type="date" 
                      value={incubationData.testDate}
                      onChange={(e) => {
                        setIncubationData({ ...incubationData, testDate: e.target.value });
                        setErrors((prev: any) => ({ ...prev, testDate: "" }));
                      }}
                      className="w-full px-6 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 outline-none focus:border-[#FF7043] focus:ring-8 focus:ring-orange-500/5 transition-all" 
                    />
                    {errors.testDate && <p className="text-[11px] text-red-500">{errors.testDate}</p>}
                  </div>
                  
                  <div className="space-y-3">
                    <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">{t("gptTest.gptTestDetails.selectPowderLot")} <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <select 
                        value={powderData.selectedLot}
                        onChange={(e) => {
                          setPowderData({ ...powderData, selectedLot: e.target.value });
                          setErrors((prev: any) => ({ ...prev, selectedLot: "" }));
                        }}
                        className="w-full px-6 py-2 border border-slate-200 rounded-lg text-sm text-slate-700 outline-none focus:bg-white focus:border-[#FF7043] transition-all appearance-none cursor-pointer"
                      >
                        <option value="">{t("gptTest.gptTestDetails.selectLotPlaceholder")}</option>
                        {availableLots.map(lot => (
                          <option key={lot.id} value={lot.id}>{lot.lotCode}</option>
                        ))}
                      </select>
                      <span className="material-symbols-outlined absolute right-6 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none">{t("gptTest.icons.expand_more")}</span>
                     {errors.selectedLot && <p className="text-[11px] text-red-500">{errors.selectedLot}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-8">
                    <div className="space-y-3">
                      <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">{t("gptTest.gptTestDetails.powderQuantity")} <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <input 
                          type="number" 
                          placeholder="0.00"
                          step="0.01"
                          value={powderData.quantityUsed}
                          onChange={(e) => {
                            setPowderData({ ...powderData, quantityUsed: e.target.value });
                            setErrors((prev: any) => ({ ...prev, quantityUsed: "" }));
                          }}
                          className="w-full px-6 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 outline-none focus:border-[#FF7043] focus:ring-8 focus:ring-orange-500/5 transition-all"
                        />
                        <span className="absolute right-12 top-1/2 -translate-y-1/2 text-[10px] font-black text-slate-300 uppercase">{t("gptTest.gptTestDetails.gms")}</span>
                       {errors.quantityUsed && <p className="text-[11px] text-red-500">{errors.quantityUsed}</p>}
                      </div>
                    </div>
                    <div className="space-y-3">
                      <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">{t("gptTest.gptTestDetails.availableInLot")}</label>
                      <div className="w-full px-6 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-bold text-slate-400 flex items-center justify-between">
                        <span>{selectedLotInfo ? selectedLotInfo.available.toFixed(2) : '--'}</span>
                        <span className="text-[10px] font-black uppercase">{t("gptTest.gptTestDetails.gms")}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
    </>      
  );
};

export default PowderLotSelection;